/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import Modal from '../common/Modal'
import { buildWorkTime, dateManipulateDay, dateToTodayElapsedSeconds, format, secToHour, secToHourMinute, secToMinute } from '../../../utils/date'
import Tips from '../common/Tips'
import { sortBy, uniqueId } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import LoadingSquare from '../common/LoadingSquare'

interface DetailList {
  start_time: string
  end_time: string
  service_name: string | null
  task_name: string | null
  time: number
  icon: string
  icon_color: string
  labeled: boolean
}

interface ApplicationList {
  service_name: string
  task_name: string | null
  icon: string
  icon_color: string
  time: number
  list: {
    start_sec: number
    end_sec: number
    time: number
  }[]
  labeled: boolean
}

interface Props {
  timeList: {
    start_time: string
    end_time: string
  }[],
  originTask: DetailList[]
  addCallback: (ranges: { start_time: string, end_time: string }[], afterFunc?: () => void) => void
  closeCallback: VoidFunction
  isDisplay: boolean
  date: string
  loading: boolean
}

const NotWorkingModal: FC<Props> = (props) => {
  const {
    timeList,
    originTask,
    addCallback,
    closeCallback,
    isDisplay,
    date,
    loading,
  } = props

  const {
    task,
  } = useSelector((state: RootState) => state)

  const TIME_LIST = Array(25).fill(null).map((_v, i) => `${i + 5}:00`)

  const [cutTimeList, setCutTimeList] = useState<{startSec: number, endSec: number}[]>([])

  const [isMoveDrag, setIsMoveDrag] = useState<boolean>(false)

  const [error, setError] = useState<{key: string, text: string}[]>([])

  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const [dragType, setDragType] = useState<string>("")
  const [basePosition, setBasePosition] = useState<number | null>(null)
  const [baseStartTime, setBaseStartTime] = useState<number>(0)
  const [baseEndTime, setBaseEndTime] = useState<number>(0)
  const [baseIndex, setBaseIndex] = useState<number | null>(null)

  const [notWorkingApplicationList, setNotWorkingApplicationList] = useState<ApplicationList[]>([])
  const [calculationTrigger, setCalculationTrigger] = useState<boolean>(true)

  const [duplicateError, setDuplicateError] = useState<string>("")

  const clickRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const customOriginTask = useMemo(() => {
    if (!originTask) return []
    return originTask.map((v) => ({ ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date) }))
  }, [originTask])

  const customCalendarSchedules = useMemo(() => {
    if (!task.taskLabeling?.calendar.schedules) return []
    return task.taskLabeling?.calendar.schedules.map((v) => ({ ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date) }))
  }, [task.taskLabeling?.calendar.schedules])

  const formatData = useMemo(() => {
    if (!customOriginTask.length) return []
    let tmp = customOriginTask.map((v) => ({ start_sec: v.start_sec, end_sec: v.end_sec }))
    cutTimeList.forEach((range) => {
      tmp = tmp.filter((data) => !(range.startSec <= data.start_sec && range.endSec >= data.end_sec)).map((data) => {
        if (range.startSec <= data.start_sec && range.endSec >= data.start_sec && range.endSec <= data.end_sec) {
          return { ...data, start_sec: range.endSec }
        }
        if (range.startSec >= data.start_sec && range.startSec <= data.end_sec && range.endSec >= data.end_sec) {
          return { ...data, end_sec: range.startSec }
        }
        return data
      })
    })
    return sortBy(tmp, "start_sec")
  }, [cutTimeList, customOriginTask])

  const buildDisplayTime = (diffSeconds: number) => {
    if (diffSeconds > 0 && diffSeconds < 60) {
      return "1分未満"
    }
    if (secToHour(diffSeconds)) {
      return `${secToHour(diffSeconds)}時間${secToMinute(diffSeconds)}分`
    }
    return `${secToMinute(diffSeconds)}分`
  }

  const resetParam = () => {
    setError([])
    setCutTimeList([])
    setIsMoveDrag(false)
    setNotWorkingApplicationList([])
  }

  const closeModal = () => {
    resetParam()
    closeCallback()
  }

  const submit = () => {
    setIsProcessing(true)
    const errorList:{key: string, text: string}[] = []
    cutTimeList.forEach((cutTime) => {
      if (cutTime.startSec >= cutTime.endSec) {
        errorList.push({ key: "range", text: "作業データの開始・終了時刻が不正です" })
      }
    })
    if (errorList.length) {
      setError(Array.from(new Set(errorList)))
      setIsProcessing(false)
      return
    }

    addCallback(
      cutTimeList.map((v) => (
        {
          start_time: v.startSec < 86400 ? `${date} ${buildWorkTime(v.startSec)}` : `${dateManipulateDay(date, 1, "YYYY-MM-DD")} ${buildWorkTime(v.startSec - 86400)}`,
          end_time: v.endSec < 86400 ? `${date} ${buildWorkTime(v.endSec)}` : `${dateManipulateDay(date, 1, "YYYY-MM-DD")} ${buildWorkTime(v.endSec - 86400)}`,
        }
      )),
      () => {
        closeCallback()
        resetParam()
        setIsProcessing(false)
      },
    )
  }

  const rulerBlock = (custom = true, height = 42) => (
    <div
      className="timeline_ruler_area"
      style={{
        width: custom ? "100%" : "",
        marginLeft: custom ? 0 : "",
        position: "absolute",
        top: 0,
        left: 0,
        height: `${height}px`,
      }}
    >
      {
        TIME_LIST.map((v) => (
          <div key={`ruler_${v}`} />
        ))
      }
      <div />
    </div>
  )

  const labelBlock = () => (
    <div
      className="timeline_label_area"
      style={{
        width: "100%",
        position: "relative",
        height: "14px",
        marginTop: "4px",
        marginBottom: "4px",
        fontSize: "10px",
        color: "#666666",
      }}
    >
      {
        TIME_LIST.map((v, i) => (
          <div
            key={v}
            style={{
              position: "absolute",
              left: `${(i / 24) * 100}%`,
              transform: "translateX(-50%)",
            }}
          >
            {v}
          </div>
        ))
      }
    </div>
  )

  const selectCutStartTime = (hour: number, minutes: number, index: number) => {
    const tmpSec = (hour * 3600) + (minutes * 60)
    if (tmpSec > cutTimeList[index].endSec) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, startSec: v.endSec } : v)))
      return
    }
    setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, startSec: tmpSec } : v)))
    setCalculationTrigger(true)
  }

  const selectCutEndTime = (hour: number, minutes: number, index: number) => {
    const tmpSec = (hour * 3600) + (minutes * 60)
    if (tmpSec > 86400 + 18000) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: 86400 + 18000 } : v)))
      return
    }
    if (tmpSec < cutTimeList[index].startSec) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: v.startSec } : v)))
      return
    }
    setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: tmpSec } : v)))
    setCalculationTrigger(true)
  }

  const hourList = (selectType: string, index: number) => {
    if (selectType === "start") {
      let tmpHourCount = secToHour(cutTimeList[index].endSec)
      if (tmpHourCount > 28) {
        tmpHourCount = 28
      } else if (tmpHourCount < 5) {
        tmpHourCount += 24
      }
      return (
        Array(((tmpHourCount - 5) + 1)).fill(null).map((_v, i) => (
          <option value={i + 5} key={`start_hour_${i + 5}`}>
            {i + 5}
          </option>
        ))
      )
    }
    const startHour = secToHour(cutTimeList[index].startSec)
    return (
      Array(29 - (startHour - 1)).fill(null).map((_v, i) => (
        <option value={i + startHour} key={`end_hour_${i + startHour}`}>
          {i + startHour}
        </option>
      ))
    )
  }

  const minutesList = (selectType: string, index: number) => {
    if (selectType === "start") {
      if (secToHour(cutTimeList[index].startSec) === secToHour(cutTimeList[index].endSec)) {
        return (
          Array(((Number(secToMinute(cutTimeList[index].endSec))) + 1)).fill(null).map((_v, i) => (
            <option value={i} key={`start_minutes_${i + 0}`}>
              {`00${i}`.slice(-2)}
            </option>
          ))
        )
      }
      return (
        Array(60).fill(null).map((_v, i) => (
          <option value={i} key={`start_minutes_${i + 0}`}>
            {`00${i}`.slice(-2)}
          </option>
        ))
      )
    }
    if (secToHour(cutTimeList[index].endSec) > 28) {
      return (
        <option value={0}>
          00
        </option>
      )
    }
    if (secToHour(cutTimeList[index].startSec) === secToHour(cutTimeList[index].endSec)) {
      const startMinutesCount = Number(secToMinute(cutTimeList[index].startSec))
      return (
        Array(60 - startMinutesCount).fill(null).map((_v, i) => (
          <option value={(i + startMinutesCount)} key={`end_minutes_${i + startMinutesCount}`}>
            {`00${(i + startMinutesCount)}`.slice(-2)}
          </option>
        ))
      )
    }
    return (
      Array(60).fill(null).map((_v, i) => (
        <option value={i} key={`start_minutes_${i + 0}`}>
          {`00${i}`.slice(-2)}
        </option>
      ))
    )
  }

  const clickSchedule = (title: string, startTime: number, endTime: number) => {
    setCutTimeList([{ startSec: startTime, endSec: endTime }])
    setCalculationTrigger(true)
  }

  const reset = () => {
    setIsMoveDrag(false)
    setDragType("")
    setBasePosition(null)
    setBaseStartTime(0)
    setBaseEndTime(0)
    setBaseIndex(null)
  }

  const endDrag = () => {
    reset()
    setCalculationTrigger(true)
  }

  const startDrag = (clickPosition: number, tmpDragType: string, index: number) => {
    setIsMoveDrag(true)
    if (tmpDragType === "start") {
      setDragType("start")
    } else if (tmpDragType === "end") {
      setDragType("end")
    } else {
      setDragType("move")
    }
    setBasePosition(clickPosition)
    setBaseStartTime(cutTimeList[index].startSec)
    setBaseEndTime(cutTimeList[index].endSec)
    setBaseIndex(index)
  }

  const moveOrDragSchedule = (clickPosition: number) => {
    if (baseIndex === null) return
    const diff = clickPosition - (basePosition || 0)
    const tmpSec = Math.ceil((diff / clickRef.current.clientWidth) * 288) * 300
    if (dragType === "start") {
      if (baseStartTime + tmpSec < 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: 18000 } : v)))
      } else if (baseStartTime + tmpSec > cutTimeList[baseIndex].endSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: v.endSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: baseStartTime + tmpSec } : v)))
      }
    }
    if (dragType === "end") {
      if (baseEndTime + tmpSec > 86400 + 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: 86400 + 18000 } : v)))
      } else if (baseEndTime + tmpSec < cutTimeList[baseIndex].startSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: v.startSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: baseEndTime + tmpSec } : v)))
      }
    }
    if (dragType === "move") {
      if (baseStartTime + tmpSec < 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: 18000, endSec: baseEndTime + tmpSec } : v)))
      } else if (baseEndTime + tmpSec > 86400 + 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: 86400 + 18000 } : v)))
      } else if (baseEndTime + tmpSec < cutTimeList[baseIndex].startSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: v.startSec } : v)))
      } else if (baseStartTime + tmpSec > cutTimeList[baseIndex].endSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: v.endSec, endSec: baseEndTime + tmpSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: baseEndTime + tmpSec } : v)))
      }
    }
  }

  const buildBlock = (targetStartSec: number, targetEndSec: number, index: number) => {
    const startPosition = ((targetStartSec - 18000) / 86400) * 100
    const blockWidth = ((targetEndSec - targetStartSec) / 86400) * 100
    return (
      <>
        <div
          style={{
            position: "absolute",
            left: `calc(${startPosition}% - 10px)`,
            top: "50%",
            width: "10px",
            height: "40px",
            padding: 0,
            borderRadius: "4px 0 0 4px",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "col-resize",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "start", index)}
          role="none"
        >
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "0",
              width: "10px",
              height: "44px",
              backgroundColor: "#00132b",
              borderRadius: "4px 0 0 4px",
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "5px",
              left: "4px",
              width: "2px",
              height: "30px",
              borderLeft: "dashed 2px rgba(255, 255, 255, 0.3)",
              zIndex: 2,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            left: `${startPosition}%`,
            top: "50%",
            width: `${blockWidth}%`,
            height: "44px",
            padding: 0,
            backgroundColor: "rgba(0, 19, 43, 0.5)",
            borderTop: "solid 2px #00132b",
            borderBottom: "solid 2px #00132b",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "move",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "move", index)}
          role="none"
        />
        <div
          style={{
            position: "absolute",
            left: `${startPosition + blockWidth}%`,
            top: "50%",
            width: "10px",
            height: "40px",
            padding: 0,
            borderRadius: "0 4px 4px 0",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "col-resize",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "end", index)}
          role="none"
        >
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "0",
              width: "10px",
              height: "44px",
              backgroundColor: "#00132b",
              borderRadius: "0 4px 4px 0",
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "5px",
              left: "4px",
              width: "2px",
              height: "30px",
              borderLeft: "dashed 2px rgba(255, 255, 255, 0.3)",
              zIndex: 2,
            }}
          />
        </div>
      </>
    )
  }

  const addPeriod = () => {
    setCutTimeList([...cutTimeList, { startSec: 36000, endSec: 39600 }])
    setCalculationTrigger(true)
  }

  const delPeriod = (index: number) => {
    setCutTimeList(cutTimeList.filter((_v, i) => i !== index))
    setCalculationTrigger(true)
  }

  useEffect(() => {
    if (timeList.length) {
      setCutTimeList(timeList.map((v) => ({ startSec: dateToTodayElapsedSeconds(v.start_time, date), endSec: dateToTodayElapsedSeconds(v.end_time, date) })))
    } else {
      setCutTimeList([])
    }
  }, [timeList])

  useEffect(() => {
    if (!customOriginTask.length || !calculationTrigger) {
      setCalculationTrigger(false)
      return
    }
    let tmp: ApplicationList[] = []
    cutTimeList.forEach((range) => {
      customOriginTask.forEach((data) => {
        let tmpTime = data.time
        let tmpStartSec = data.start_sec
        let tmpEndSec = data.end_sec
        let is_target = false
        if (range.startSec <= data.start_sec && range.endSec >= data.end_sec) {
          tmpTime = data.time
          tmpStartSec = data.start_sec
          tmpEndSec = data.end_sec
          is_target = true
        } else if (range.startSec <= data.start_sec && range.endSec >= data.start_sec && range.endSec <= data.end_sec) {
          tmpTime = range.endSec - data.start_sec
          tmpStartSec = data.start_sec
          tmpEndSec = range.endSec
          is_target = true
        } else if (range.startSec >= data.start_sec && range.startSec <= data.end_sec && range.endSec >= data.end_sec) {
          tmpTime = data.end_sec - range.startSec
          tmpStartSec = range.startSec
          tmpEndSec = data.end_sec
          is_target = true
        } else if (range.startSec >= data.start_sec && range.endSec <= data.end_sec) {
          tmpTime = range.endSec - range.startSec
          tmpStartSec = range.startSec
          tmpEndSec = range.endSec
          is_target = true
        }
        if (is_target) {
          if (tmp.find((v) => v.service_name === (data.service_name || "不明なアプリ") && v.task_name === data.task_name)) {
            tmp = tmp.map((v) => (v.service_name === (data.service_name || "不明なアプリ") && v.task_name === data.task_name
              ? ({ ...v, time: v.time + tmpTime, list: [...v.list, { start_sec: tmpStartSec, end_sec: tmpEndSec, time: tmpTime }] })
              : v
            ))
          } else {
            tmp.push({
              service_name: data.service_name || "不明なアプリ",
              task_name: data.task_name || "タイトルなし",
              icon: data.icon,
              icon_color: data.icon_color,
              time: tmpTime,
              list: [
                { start_sec: tmpStartSec, end_sec: tmpEndSec, time: tmpTime },
              ],
              labeled: data.labeled,
            })
          }
        }
      })
    })
    setNotWorkingApplicationList(tmp.filter((v) => v.time).sort((a, b) => a.list[0].start_sec - b.list[0].start_sec))
    setCalculationTrigger(false)
  }, [calculationTrigger])

  useEffect(() => {
    setTimeout(() => {
      setCalculationTrigger(true)
    }, 200)
  }, [customOriginTask])

  useEffect(() => {
    if (!cutTimeList.length) {
      setDuplicateError("")
      return
    }
    const isError = cutTimeList.some((v, i) => (
      cutTimeList.filter((_, j) => j > i).some((w) => {
        if ((v.startSec < w.endSec && v.endSec > w.startSec)) {
          setDuplicateError("業務外範囲は重複して登録することができません")
          return true
        }
        return false
      })
    ))
    if (!isError) setDuplicateError("")
  }, [cutTimeList])

  return (
    <>
      <Modal
        title="業務外作業時間範囲設定"
        show={isDisplay}
        wrapperClass="w900"
        closeCallback={closeModal}
        closeable={!isProcessing}
      >
        <>
          {
            loading ? (
              <>
                <LoadingSquare>
                  <>
                    Now Loading…
                  </>
                </LoadingSquare>
              </>
            ) : (
              <>
                <div
                  role="none"
                  onMouseMove={(e) => isMoveDrag && moveOrDragSchedule(e.clientX - clickRef.current.getBoundingClientRect().left)}
                  onMouseUp={() => isMoveDrag && endDrag()}
                  onMouseLeave={() => isMoveDrag && endDrag()}
                >
                  <div className="header_area" style={{ userSelect: "none" }}>
                    <div className="text_area">
                      <div>
                        業務外作業時間の範囲を選択・登録します。
                      </div>
                    </div>
                  </div>
                  <div className="item" style={{ userSelect: "none" }}>
                    <div className="title" style={{ justifyContent: "space-between" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        範囲選択
                        <div className="required">
                          必須
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#06a2a2",
                          }}
                        >
                          範囲選択後の業務開始・終了時間
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#666666",
                          }}
                        >
                          {`${formatData.length ? secToHourMinute(Math.min(...formatData.map((v) => v.start_sec))) : "なし"} ~ ${formatData.length ? secToHourMinute(Math.max(...formatData.map((v) => v.end_sec))) : "なし"}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px", justifyContent: "center", flexFlow: "column", position: "relative", minHeight: "26px" }}>
                      {
                        cutTimeList.map((v, i) => (
                          <div
                            key={`${v.startSec}_${v.endSec}_${i + 1}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <div style={{ display: "flex", gap: "4px", alignItems: "center", fontWeight: "bold" }}>
                              <select
                                style={{
                                  border: "solid 1px #CCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  appearance: "auto",
                                }}
                                onChange={(e) => selectCutStartTime(Number(e.target.value), Number(secToMinute(v.startSec)), i)}
                                value={secToHour(v.startSec)}
                              >
                                {hourList("start", i)}
                              </select>
                              :
                              <select
                                style={{
                                  border: "solid 1px #CCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  appearance: "auto",
                                }}
                                onChange={(e) => selectCutStartTime(Number(secToHour(v.startSec)), Number(e.target.value), i)}
                                value={secToMinute(v.startSec)}
                              >
                                {minutesList("start", i)}
                              </select>
                            </div>
                            ~
                            <div style={{ display: "flex", gap: "4px", alignItems: "center", fontWeight: "bold" }}>
                              <select
                                style={{
                                  border: "solid 1px #CCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  appearance: "auto",
                                }}
                                onChange={(e) => selectCutEndTime(Number(e.target.value), Number(secToMinute(v.endSec)), i)}
                                value={secToHour(v.endSec)}
                              >
                                {hourList("end", i)}
                              </select>
                              :
                              <select
                                style={{
                                  border: "solid 1px #CCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  appearance: "auto",
                                }}
                                onChange={(e) => selectCutEndTime(Number(secToHour(v.endSec)), Number(e.target.value), i)}
                                value={secToMinute(v.endSec)}
                              >
                                {minutesList("end", i)}
                              </select>
                            </div>
                            の範囲が選択されています
                            <div
                              onClick={() => delPeriod(i)}
                              role="none"
                              style={{
                                color: "#FF0000",
                                cursor: "pointer",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                            </div>
                          </div>
                        ))
                      }
                      <div
                        style={{
                          position: "absolute",
                          top: "-4px",
                          right: cutTimeList.length ? "0" : "50%",
                          transform: cutTimeList.length ? "" : "translateX(50%)",
                        }}
                      >
                        <button
                          type="button"
                          onClick={addPeriod}
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                          }}
                        >
                          {cutTimeList.length ? "業務外範囲を増やす" : "業務外範囲を設定する"}
                        </button>
                      </div>
                    </div>
                    <div className="labeling_legend_area" style={{ position: "relative", marginBottom: "8px" }}>
                      <div className="timeline_legend">
                        <div
                          className="legend_color"
                          style={{
                            backgroundColor: "#4570f3",
                          }}
                        />
                        <div className="legend_text">
                          作業データ
                        </div>
                      </div>
                      <div className="timeline_legend">
                        <div
                          className="legend_color"
                          style={{
                            backgroundColor: "rgba(0, 19, 43, 0.5)",
                            borderRight: "solid 2px #00132b",
                            borderLeft: "solid 2px #00132b",
                          }}
                        />
                        <div className="legend_text">
                          業務外範囲
                        </div>
                      </div>
                      {
                        !!duplicateError && (
                          <div
                            style={{
                              color: "#FF0000",
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                          >
                            {duplicateError}
                          </div>
                        )
                      }
                    </div>
                    <div
                      className="timeline"
                      style={{
                        marginBottom: "0px",
                        height: "44px",
                      }}
                    >
                      <div
                        ref={clickRef}
                        className="timeline_row"
                        style={{
                          borderBottom: "none",
                          height: "42px",
                        }}
                      >
                        {rulerBlock()}
                        {
                          customOriginTask.map((value) => (
                            <div
                              key={`not_working_modal_${value.start_time}_${value.end_time}`}
                              className="timeline_bar not_working"
                              style={{
                                left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                width:
                                `${(((value.end_sec - value.start_sec) / 86400)) * 100}%`,
                              }}
                            />
                          ))
                        }
                        {
                          cutTimeList.map((v, i) => (
                            <React.Fragment key={`range_${v.startSec}_${v.endSec}_${i + 1}`}>
                              {buildBlock(v.startSec, v.endSec, i)}
                            </React.Fragment>
                          ))
                        }
                      </div>
                    </div>
                    {labelBlock()}
                    {
                      error.find((v) => v.key === "range")?.text && (
                        <div className="error">
                          {error.find((v) => v.key === "range")?.text}
                        </div>
                      )
                    }
                    {
                      task.taskLabeling?.calendar.schedules.length ? (
                        <>
                          <div className="timeline calendar">
                            <div className="timeline_row" style={{ height: "38px" }}>
                              {rulerBlock()}
                              {
                                customCalendarSchedules.map((value) => (
                                  <React.Fragment key={`calendar_modal_${value.start_time}`}>
                                    <div
                                      className="timeline_item"
                                      style={{
                                        left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                        width:
                                        `${((value.end_sec - value.start_sec) / 86400) * 100 > 0.2 ? ((value.end_sec - value.start_sec) / 86400) * 100 : 0.2}%`,
                                      }}
                                      role="none"
                                      onClick={
                                        () => clickSchedule(value.title, value.start_sec, value.end_sec)
                                      }
                                    >
                                      <Tips
                                        description={`${format(value.start_time, "HH時mm分")} ~ ${format(value.end_time, "HH時mm分")} ${value.title}`}
                                        className=""
                                        icon={false}
                                      >
                                        <>
                                        </>
                                      </Tips>
                                    </div>
                                  </React.Fragment>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      ) : (
                        <></>
                      )
                    }
                    {
                      notWorkingApplicationList.some((v) => v.labeled) && (
                        <div
                          style={{
                            border: "solid 1px #c61516",
                            borderRadius: "4px",
                            padding: "4px",
                            color: "#c61516",
                            backgroundColor: "#ffdfdf",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                          }}
                        >
                          指定した範囲に仕分け済み作業データが含まれています！
                        </div>
                      )
                    }
                  </div>
                  <div className="item" style={{ userSelect: "none", height: "220px" }}>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginBottom: "8px",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px 0",
                        }}
                      >
                        業務外作業時間に以下の作業データが含まれています
                      </div>
                      {labelBlock()}
                      <div
                        className="timeline"
                        style={{
                          border: "dashed 1px #b4e5e2",
                          backgroundColor: "#FFFFFF",
                          padding: "0",
                          height: "180px",
                          overflowY: "auto",
                          display: "flex",
                          flexFlow: "column",
                        }}
                      >
                        {
                          !notWorkingApplicationList.length && (
                            <div
                              style={{
                                height: "180px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "20px",
                                color: "#AAAAAA",
                              }}
                            >
                              業務外作業時間に含まれる作業データはありません
                            </div>
                          )
                        }
                        {
                          notWorkingApplicationList.map((v) => (
                            <div
                              className="timeline_row"
                              key={uniqueId()}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                position: "relative",
                                minHeight: "32px",
                                borderBottom: "1px solid #CCCCCC",
                              }}
                            >
                              {rulerBlock(true, 32)}
                              {
                                date && v.list.map((value) => (
                                  <div
                                    key={`timeline_${value.start_sec}`}
                                    className="timeline_bar not_working"
                                    style={{
                                      left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                      width:
                                      `${((value.end_sec - value.start_sec) / 86400) * 100 > 0.2 ? ((value.end_sec - value.start_sec) / 86400) * 100 : 0.2}%`,
                                      position: "absolute",
                                      top: "3px",
                                      height: "24px",
                                    }}
                                  >
                                    <Tips
                                      description={`${secToHourMinute(value.start_sec)}~${secToHourMinute(value.end_sec)}`}
                                      className=""
                                      icon={false}
                                    >
                                      <></>
                                    </Tips>
                                  </div>
                                ))
                              }
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  marginLeft: "8px",
                                  fontSize: "10px",
                                  zIndex: 1,
                                  maxWidth: "90%",
                                }}
                                title={v.task_name || ""}
                              >
                                <div
                                  className="icon_area"
                                  style={{
                                    color: v.icon_color,
                                    fontSize: "12px",
                                  }}
                                >
                                  {
                                    v.icon && (
                                      <FontAwesomeIcon
                                        icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                        fixedWidth
                                      />
                                    )
                                  }
                                </div>
                                <div
                                  style={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {v.service_name}
                                </div>
                                <div
                                  style={{
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                  }}
                                >
                                  {v.task_name}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  marginRight: "8px",
                                  zIndex: 1,
                                  fontSize: "10px",
                                  color: "#999999",
                                }}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                </div>
                                <div>
                                  {buildDisplayTime(v.time)}
                                </div>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </>
        <>
          <button
            type="button"
            className="button cancel"
            onClick={closeModal}
            disabled={isProcessing}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button"
            onClick={submit}
            disabled={!!error.length || isProcessing || !!duplicateError}
          >
            {
              isProcessing ? (
                <>
                  <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                </>
              ) : (
                <>
                  登録
                </>
              )
            }
          </button>
        </>
      </Modal>
    </>
  )
}

export default NotWorkingModal
